import { cGradeOf } from '@shared/levels';
import { api } from '../api/client.js';
import {
  bareLevel,
  boxLayout,
  DOC_STYLES,
  docFooter,
  factBox,
  levelLabel,
  loadPdfMake,
  monthsTH,
  PAGE_MARGINS,
  pdfFileName,
  rowLayout,
  sinceText,
  stamp,
  td,
  thaiDate,
  untilText,
  type Row,
} from './pdfKit.js';
import type { Position, PositionHolder, PositionHolders } from '@shared/types';
import type { PersonProfile } from '@shared/views';

/**
 * สร้างไฟล์ PDF ของ "หนึ่งอัตรา"
 *
 * ────────────────────────────────────────────────────────────────
 *  ต่างจากเอกสารประวัติรายบุคคลยังไง
 * ────────────────────────────────────────────────────────────────
 *
 * profilePdf.ts ตอบคำถาม "คนคนนี้เป็นใคร ผ่านอะไรมาบ้าง"
 * ไฟล์นี้ตอบคำถาม "อัตรานี้เป็นยังไง" ซึ่งเป็นคนละคำถามและมีผู้อ่านคนละกลุ่ม
 *
 * คนที่กดปุ่มนี้กำลังทำเรื่องเสนออัตรา — เขาต้องการทะเบียนของอัตรา สถานะว่าง/มีผู้ครอง
 * ใครครองอยู่และจะพ้นเมื่อไร และใครเคยครองมาก่อน ทั้งหมดในกระดาษแผ่นเดียวที่แนบเรื่องได้
 * ส่วนข้อมูลประกอบของตัวบุคคล (การศึกษา · อบรม · เครื่องราชฯ) อยู่ในเอกสารประวัติ
 * ซึ่งกดได้จากหน้าประวัติรายบุคคล
 *
 * ⚠ ห้ามยัดข้อมูลประกอบของผู้ครองลงในไฟล์นี้เพื่อความ "ครบ"
 *   เอกสารที่ตอบสองคำถามพร้อมกันจะยาวจนไม่มีใครอ่านหน้าสอง และที่แย่กว่าคือ
 *   มันจะกลายเป็นการแจกประวัติส่วนตัวของคนหนึ่งคนไปกับเรื่องเสนออัตรา
 *   ซึ่งไม่ใช่สิ่งที่คนกดปุ่มตั้งใจจะทำ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมดึงข้อมูลเพิ่มตอนกด ไม่ใช่รับมาทั้งก้อน
 * ────────────────────────────────────────────────────────────────
 *
 * ประวัติผู้ครองกับรายชื่อผู้เคยครองอยู่คนละ endpoint และหน้าจอโหลดมันแบบ lazy
 * (กล่อง "ผู้ที่เคยครองอัตรานี้" ยิงคำขอเฉพาะตอนกางออก) ถ้าปุ่มนี้ต้องรอให้ผู้ใช้
 * กางทุกกล่องก่อนถึงจะได้ไฟล์ครบ ก็จะได้ไฟล์ที่ครบบ้างไม่ครบบ้างโดยที่ไม่มีอะไรบอก
 *
 * ⚠ ทั้งสองคำขอห้ามทำให้ทั้งไฟล์ล้มเหลว — ถ้าดึงไม่สำเร็จ ให้พิมพ์บรรทัดบอกไว้ในเอกสาร
 *   ว่าส่วนนั้นดึงไม่ได้ ดีกว่าโยน error แล้วผู้ใช้ไม่ได้อะไรเลยทั้งที่ข้อมูลอัตรามีครบ
 */
export async function downloadPositionPdf(p: Position): Promise<void> {
  const personaId = p.incumbent?.personaId ?? null;

  /* allSettled ไม่ใช่ all — ดูเหตุผลที่หัวไฟล์ ส่วนที่ดึงไม่ได้กลายเป็นบรรทัดหมายเหตุ
     ไม่ใช่ทำให้ปุ่มขึ้นว่าสร้างไฟล์ไม่สำเร็จทั้งที่ข้อมูลอัตราอยู่ในมือแล้ว */
  const [profileRes, holdersRes] = await Promise.allSettled([
    personaId ? api.profile(personaId) : Promise.resolve(null),
    api.positionHolders(p.posId),
  ]);

  const profile = profileRes.status === 'fulfilled' ? profileRes.value : null;
  const holders = holdersRes.status === 'fulfilled' ? holdersRes.value : null;

  const pdfMake = await loadPdfMake();
  pdfMake
    .createPdf(buildDocument(p, profile, holders, {
      profileFailed: personaId !== null && profileRes.status === 'rejected',
      holdersFailed: holdersRes.status === 'rejected',
    }))
    .download(fileName(p));
}

function fileName(p: Position): string {
  return pdfFileName(['อัตรา', p.posNo ?? p.posId, p.title]);
}

/* ================================================================
   โครงเอกสาร
   ================================================================ */

/**
 * ⚠ export ไว้เพื่อให้สคริปต์ตรวจ (scripts/check-position-pdf.ts) สร้างเอกสารจริง
 *   จากข้อมูลจริงได้โดยไม่ต้องเปิดเบราว์เซอร์ — หน้าจอไม่ได้เรียกตัวนี้ตรง ๆ
 *   เป็นฟังก์ชันบริสุทธิ์ ไม่แตะเครือข่าย จึงรันในสคริปต์ Node ได้
 */
export function buildDocument(
  p: Position,
  profile: PersonProfile | null,
  holders: PositionHolders | null,
  failed: { profileFailed: boolean; holdersFailed: boolean },
) {
  const c = cGradeOf(p.level);
  const who = p.incumbent;

  return {
    info: {
      title: `อัตรา ${p.posNo ?? p.posId} ${p.title}`,
      subject: 'ทะเบียนอัตรา สถานะ และผู้ครอง',
      creator: 'YANA ระบบวิเคราะห์และวางแผนอัตรากำลัง',
    },
    pageSize: 'A4',
    pageMargins: PAGE_MARGINS,
    defaultStyle: { font: 'Sarabun', fontSize: 10.5, lineHeight: 1.35, color: '#101a2c' },
    footer: docFooter(),

    content: [
      /* ---------- หัวเอกสาร ---------- */
      { text: p.title, style: 'h1' },
      {
        text: [levelLabel(p.level), c ? `  ·  เทียบ${c}` : '', p.line ? `  ·  สายงาน${p.line}` : '']
          .filter(Boolean)
          .join(''),
        style: 'lead',
      },
      { text: (p.orgPath.length > 0 ? p.orgPath : [p.unit]).join('  ›  '), style: 'unit' },
      {
        text: `${p.posNo ? `เลขที่ตำแหน่ง ${p.posNo}` : 'ไม่มีเลขที่ตำแหน่ง'}  ·  ${stamp()}`,
        style: 'small',
        margin: [0, 4, 0, 0] as [number, number, number, number],
      },

      /* ---------- สถานะ ----------
         อยู่บนสุดก่อนทะเบียน เพราะเป็นสิ่งเดียวที่คนเปิดเอกสารนี้ต้องรู้ให้ได้
         ⚠ อัตราที่มีผู้รักษาการยังเป็น "ว่าง" — ต้องขึ้นคำว่าว่างก่อนเสมอ
           แล้วจึงบอกว่าใครรักษาการ ถ้าสลับลำดับ ผู้อ่านจะนับอัตรานี้ว่ามีคนแล้ว */
      {
        text: statusLine(p),
        style: p.vacant ? 'alert' : 'warn',
        margin: [0, 10, 0, 0] as [number, number, number, number],
      },

      /* ---------- ทะเบียนอัตรา ---------- */
      { text: 'ข้อมูลอัตรา', style: 'h2' },
      keyValueTable([
        ['เลขที่ตำแหน่ง', p.posNo ?? '—'],
        ['ชื่อตำแหน่ง', p.title],
        ...(p.assignment ? [['หน้าที่ที่ได้รับมอบหมาย', p.assignment] as [string, string]] : []),
        ['สายงาน', p.line || '—'],
        ['ระดับ', p.level ? `${levelLabel(p.level)}${c ? ` · เทียบ${c}` : ''}` : '—'],
        ['ประเภทบุคลากร', p.category],
        ['กลุ่มตำแหน่งตามต้นทาง', p.positionGroup ?? '—'],
        ['สถานะ', p.vacant ? 'ว่าง' : 'มีผู้ครอง'],
        ['สังกัด', (p.orgPath.length > 0 ? p.orgPath : [p.unit]).join(' › ')],
      ]),

      /* ---------- ผู้ครอง หรือ สถานะว่าง ---------- */
      ...(who ? incumbentBlock(who, profile, failed.profileFailed) : vacancyBlock(p)),

      /* ---------- ผู้เคยครอง ---------- */
      { text: 'ผู้ที่เคยครองอัตรานี้', style: 'h2' },
      ...holdersBlock(holders, failed.holdersFailed),
    ],

    styles: DOC_STYLES,
  };
}

/** บรรทัดสถานะบนหัวเอกสาร */
function statusLine(p: Position): string {
  if (!p.vacant) {
    const mark = p.incumbent?.retireInYears;
    const soon = mark !== null && mark !== undefined && mark <= 5
      ? `  ·  ผู้ครองจะเกษียณ ${untilText(p.incumbent?.retireDate ?? null)}`
      : '';
    return `อัตรานี้มีผู้ครอง${soon}`;
  }

  const since = p.vacantMonths === null
    ? 'ไม่ทราบว่าว่างตั้งแต่เมื่อไร'
    : `ว่างมาแล้ว ${monthsTH(p.vacantMonths)}`;
  const acting = p.acting ? `  ·  มีผู้รักษาการ ${p.acting.name}` : '';
  return `อัตรานี้ว่าง  ·  ${since}${acting}`;
}

/* ================================================================
   ผู้ครองปัจจุบัน
   ================================================================ */

function incumbentBlock(
  who: NonNullable<Position['incumbent']>,
  profile: PersonProfile | null,
  profileFailed: boolean,
) {
  const items = profile?.careerHistory.items ?? [];

  return [
    { text: 'ผู้ครองปัจจุบัน', style: 'h2' },
    { text: who.name, style: 'lead', margin: [0, 0, 0, 6] as [number, number, number, number] },

    /* สี่ค่านี้คือชุดเดียวกับที่หน้าจอแสดงบนแถบตัวตน — ตั้งใจให้ตรงกันเป๊ะ
       ผู้ใช้ที่ปรินต์ออกมาแล้วเทียบกับจอต้องไม่เจอเลขที่ต่างกันแม้แต่ช่องเดียว */
    {
      table: {
        widths: ['*', '*', '*', '*'],
        body: [[
          factBox('อายุ', who.age != null ? `${who.age} ปี` : '—'),
          factBox(
            'เกษียณอายุราชการ',
            thaiDate(who.retireDate),
            who.retireFiscalYear ? `อัตราว่าง 1 ต.ค. ${who.retireFiscalYear} · ${untilText(who.retireDate)}` : '',
          ),
          factBox('อายุราชการ', sinceText(who.entryDate), `บรรจุ ${thaiDate(who.entryDate)}`),
          factBox('ครองอัตรานี้มาแล้ว', sinceText(who.occupyDate), `ตั้งแต่ ${thaiDate(who.occupyDate)}`),
        ]],
      },
      layout: boxLayout,
    },

    /* ⚠ ป้ายนี้แปลว่า "ผ่านเกณฑ์เบื้องต้น" ไม่ใช่ "ควรได้เลื่อน"
       ต้องเขียนออกมาให้ครบประโยคในเอกสาร เพราะไฟล์ที่ถูกแนบเรื่องไม่มีคำอธิบายบนจอตามไปด้วย */
    who.promotionEligible
      ? {
          text: 'ผ่านเกณฑ์ระยะเวลาการเลื่อนระดับเบื้องต้น — เกณฑ์เต็มมีข้อที่ข้อมูลชุดนี้ตอบไม่ได้',
          style: 'small',
          margin: [0, 6, 0, 0] as [number, number, number, number],
        }
      : {},

    { text: 'เส้นทางราชการของผู้ครอง', style: 'h2' },
    ...(profileFailed
      ? [{ text: 'ดึงประวัติการดำรงตำแหน่งไม่สำเร็จ จึงไม่มีส่วนนี้ในเอกสาร', style: 'none' }]
      : [historyTable(items)]),
  ];
}

/**
 * ตารางเส้นทางราชการ เรียงจากใหม่ไปเก่า
 *
 * ใช้ตาราง ไม่ใช่แกนเวลาแบบบนหน้าจอ — บนกระดาษที่ข้ามหน้าได้ หัวตารางที่ซ้ำทุกหน้า
 * อ่านง่ายกว่าเส้นแกนที่ขาดตอนกลางหน้า (เหตุผลเดียวกับ profilePdf.ts)
 */
function historyTable(items: PersonProfile['careerHistory']['items']) {
  if (items.length === 0) {
    return { text: 'ไม่มีประวัติการดำรงตำแหน่งในข้อมูลชุดนี้', style: 'none' };
  }

  const body: Row[] = [
    [td('ตำแหน่ง', 'th'), td('ระดับ', 'th'), td('ช่วงเวลา', 'th'), td('ระยะเวลา', 'th')],
  ];

  for (const h of [...items].reverse()) {
    /* ⚠ "รักษาการ" ต้องเขียนออกมา ไม่ใช่แสดงเหมือนการครองตำแหน่ง
       บนเอกสารที่นับอายุงานสะสม สองอย่างนี้มีน้ำหนักไม่เท่ากันตามกฎ ก.พ. */
    const tags = [h.acting ? 'รักษาการ' : '', h.isCurrent ? 'ปัจจุบัน' : ''].filter(Boolean);
    const title = [
      h.title,
      tags.length ? ` (${tags.join(' · ')})` : '',
      h.orderNo ? `\nคำสั่งที่ ${h.orderNo}` : '',
    ].join('');

    body.push([
      td(title),
      td(bareLevel(h.level ?? h.legacyLevel ?? h.nonCivilServiceLevel)),
      td(`${thaiDate(h.start)} – ${h.end ? thaiDate(h.end) : 'ปัจจุบัน'}`),
      /* ห้ามให้ระยะเวลาขึ้นบรรทัดใหม่ — '1 ปี 10 เดือน' ที่แตกสองบรรทัด
         ทำให้ทั้งแถวสูงขึ้นเพื่อคำเดียว และตารางดูขาดเป็นช่วง ๆ */
      td(monthsTH(h.months), undefined, true),
    ]);
  }

  return {
    /* ความกว้างสามคอลัมน์ท้ายวัดจากค่าที่ยาวที่สุดที่มีอยู่จริงในข้อมูล
         ระดับ    'ชำนาญการพิเศษ'
         ช่วงเวลา '27 ธ.ค. 2545 – 17 พ.ย. 2547'
         ระยะเวลา '1 ปี 10 เดือน' */
    table: { headerRows: 1, widths: ['*', 86, 142, 72], body },
    layout: rowLayout,
  };
}

/* ================================================================
   อัตราว่าง
   ================================================================ */

function vacancyBlock(p: Position) {
  return [
    { text: 'สถานะอัตรา', style: 'h2' },
    /* ⚠ ไม่มีแถว "สถานะ: ว่าง" ซ้ำตรงนี้ — ตาราง "ข้อมูลอัตรา" ด้านบนมีอยู่แล้ว
       ค่าเดียวกันสองที่ในหน้าเดียว ทำให้ผู้อ่านเริ่มเทียบว่าสองตารางต่างกันตรงไหน
       ทั้งที่มันคือค่าเดียวกัน (กับดักเดียวกับที่ hideIdentity ของ PersonCard แก้อยู่) */
    keyValueTable([
      ['ว่างตั้งแต่', thaiDate(p.vacantSince)],
      ['ว่างมาแล้ว', p.vacantMonths === null ? '—' : monthsTH(p.vacantMonths)],
      ['ผู้ครองคนสุดท้ายตามทะเบียน', p.lastHolder?.name ?? '—'],
      ['วันสุดท้ายที่ครอง', thaiDate(p.lastHolder?.until ?? null)],
      ['เหตุที่พ้นจากตำแหน่ง', p.lastHolder?.reason ?? '—'],
      /* ⚠ ผู้รักษาการต้องอยู่ในเอกสาร แต่ต้องอยู่ "ใต้" บรรทัดสถานะว่างเสมอ
         ไม่ใช่แทนที่มัน — คนรักษาการไม่ได้ครองอัตรา อัตรายังว่างอยู่ */
      ['ผู้รักษาการในตำแหน่ง', p.acting?.name ?? '—'],
      [
        'ตำแหน่งหลักของผู้รักษาการ',
        p.acting ? [p.acting.homeTitle, p.acting.homeLevel].filter(Boolean).join(' · ') || '—' : '—',
      ],
      ['รักษาการตั้งแต่', thaiDate(p.acting?.since ?? null)],
    ]),
  ];
}

/* ================================================================
   ผู้เคยครองอัตรานี้
   ================================================================ */

function holdersBlock(holders: PositionHolders | null, holdersFailed: boolean) {
  if (holdersFailed) {
    return [{ text: 'ดึงประวัติผู้ครองอัตรานี้ไม่สำเร็จ จึงไม่มีส่วนนี้ในเอกสาร', style: 'none' }];
  }

  const rows = holders?.holders ?? [];
  if (rows.length === 0) {
    return [
      {
        /* ⚠ "ไม่มีข้อมูล" กับ "ไม่เคยมีใครครอง" คนละเรื่อง — ต้องเขียนให้ตรงว่าเป็นอย่างแรก
           ไฟล์ที่เขียนว่าไม่เคยมีใครครองทั้งที่ระบบแค่ไม่มีข้อมูล คือข้อมูลผิดที่ระบบพิมพ์ให้เอง */
        text: 'ไม่มีประวัติผู้ครองอัตรานี้ในชุดข้อมูล — ไม่ได้แปลว่าไม่เคยมีผู้ครอง',
        style: 'none',
      },
    ];
  }

  const body: Row[] = [
    [td('ชื่อ-สกุล', 'th'), td('ตำแหน่ง', 'th'), td('ระดับ', 'th'), td('ช่วงเวลา', 'th')],
  ];

  for (const h of rows) {
    body.push([
      td(holderName(h)),
      td(h.title ?? '—'),
      td(bareLevel(h.level)),
      td(`${thaiDate(h.startDate)} – ${h.endDate ? thaiDate(h.endDate) : 'ปัจจุบัน'}`),
    ]);
  }

  return [{ table: { headerRows: 1, widths: ['*', '*', 86, 142], body }, layout: rowLayout }];
}

/** ชื่อผู้เคยครอง — name เป็น null แปลว่าตารางบุคคลไม่มีรหัสนี้ ไม่ใช่ "ไม่มีชื่อ" */
function holderName(h: PositionHolder): string {
  const tags = [h.acting ? 'รักษาการ' : '', h.current ? 'ปัจจุบัน' : ''].filter(Boolean);
  const base = h.name ?? `ไม่พบชื่อในทะเบียนบุคคล (รหัส ${h.personaId})`;
  return tags.length ? `${base} (${tags.join(' · ')})` : base;
}

/* ================================================================
   ตารางป้าย-ค่า
   ================================================================ */

/**
 * ตารางสองคอลัมน์ ป้ายซ้าย ค่าขวา
 *
 * ⚠ คอลัมน์ป้ายกว้างคงที่ 170 จุด ไม่ใช่ auto — ป้ายที่ยาวที่สุดในเอกสารนี้คือ
 *   "ตำแหน่งหลักของผู้รักษาการ" ถ้าปล่อยให้ยืดตามเนื้อหา ตารางสองก้อนในหน้าเดียว
 *   จะมีเส้นแบ่งคอลัมน์คนละตำแหน่ง แล้วอ่านเหมือนเอกสารสองฉบับมาต่อกัน
 */
function keyValueTable(rows: Array<[string, string]>) {
  return {
    table: { widths: [170, '*'], body: rows.map(([k, v]) => [td(k, 'th'), td(v)]) },
    layout: rowLayout,
  };
}
